import { RouteComponentProps } from 'react-router';
import {
  AppHeader,
  CardModal,
  IconArrowLeft,
  IconButton,
  Session,
  UpdateContractWidget,
  useEvent,
} from '@mindly/ui-components';
import { useSignAgreementsContext } from '../../context/SignAgreementsContext/SignAgreements.context';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { IonPage, useIonViewWillEnter } from '@ionic/react';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import { SignAgreementWidget } from '../../widgets';
import useToast from '../../context/ToastContext/ToastContext';
import { ConsultationType } from '../types';
import image from '../../shared/assets/images/file-text-front-color.webp';
import { useAuthFlow } from '../../shared';
import { useSelector } from 'react-redux';
import { userSelector } from '../../redux/user/selector';
import { isVideoCallActiveSelector } from 'src/redux/videoCall/selector';
import { useVideoCall } from 'src/shared/hooks/useVideoCall';

type UpdateContractPageProps = RouteComponentProps<
  {},
  {},
  {
    selectedSession?: ConsultationType;
  }
>;

export function UpdateContractPage({ history }: UpdateContractPageProps) {
  const {
    openModal,
    currentOpenModal,
    closeModal,
    isSignAgreementOpen,
    updateContractPresentingElement,
    createContractBySpecialistId,
    fetchStatus,
  } = useSignAgreementsContext();
  const currentUser = useSelector(userSelector);
  const isVideoCallActive = useSelector(isVideoCallActiveSelector);
  const { t } = useTranslation(['app', 'schedule']);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { authUser } = useAuthFlow();
  const { showToast } = useToast();
  const data = history?.location?.state;

  const { handleModalVideoClick: handleModalVideoClickHook, isLoading: isVideoCallLoading } = useVideoCall();

  const selectedSession = data?.selectedSession;

  useIonViewWillEnter(() => {
    if (!authUser) {
      history.push(PublicRoutes.AUTH);
      return;
    }
  }, []);

  const handleModalVideoClick = useCallback(async () => {
    if (isVideoCallActive) return;

    if (!selectedSession) return;

    handleModalVideoClickHook(selectedSession as Session);
  }, [selectedSession, isVideoCallActive, handleModalVideoClickHook]);

  const onApply = useEvent(async () => {
    if (!selectedSession?.psychologist_id) return;
    if (!isAccepted) return;
    try {
      if (currentUser?.country && currentUser?.dob) {
        setIsLoading(true);
        await createContractBySpecialistId(selectedSession?.psychologist_id);
        handleModalVideoClick();
      } else {
        history.push(PrivateRoutes.NEED_UPDATE_PERSONAL_DATA);
      }
    } catch (error) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  });

  const isAllLoading = useMemo(() => isLoading || isVideoCallLoading, [isLoading, isVideoCallLoading]);

  if (!selectedSession) return null;

  return (
    <IonPage ref={updateContractPresentingElement}>
      <AppHeader className="flex flex-row items-start mb-4 cursor-pointer">
        <IconButton icon={<IconArrowLeft />} onClick={() => history.back()} />
      </AppHeader>
      <UpdateContractWidget
        handlePressLink={() => openModal('update-contract')}
        isLoading={!isAccepted && (fetchStatus === 'pending' || isAllLoading)}
        onApply={onApply}
        isAccepted={isAccepted}
        avatar={selectedSession?.psychologist_avatar}
        image={image}
        agreementText={[
          t('app:confirm_agreement.text.0'),
          t('app:confirm_agreement.text.1'),
          t('app:confirm_agreement.text.2'),
        ]}
        applyText={t('schedule:update_agreement_alert.button')}
        heading={t('schedule:update_agreement_alert.title', {
          therapistName: selectedSession?.therapist?.full_name_in_instrumental_case,
        })}
        setIsAccepted={setIsAccepted}
      />
      <CardModal
        presentingElement={
          updateContractPresentingElement.current ? updateContractPresentingElement.current : undefined
        }
        isOpen={isSignAgreementOpen && currentOpenModal === 'update-contract'}
        onDidDismiss={closeModal}
      >
        <SignAgreementWidget
          specialistId={selectedSession.psychologist_id}
          type="new"
          contractStatus="not_signed"
          onApply={() => {
            setIsAccepted(true);
            closeModal();
          }}
        />
      </CardModal>
    </IonPage>
  );
}
